/**
 * Turning a cluster into a map.
 *
 * The shape is a *trunkless* tree — no single root, because a Proxmox estate
 * does not have one. Each PVE host is the root of its own left-to-right tree,
 * and the trees are stacked down the canvas.
 *
 * The important idea is **nesting by gateway**. A guest with a leg on the
 * uplink bridge and a leg on an internal bridge is a router: everything on that
 * internal bridge sits *behind* it. So instead of hanging every bridge off the
 * host as a sibling, we nest:
 *
 *     pve8 ── vmbr0 ── FW3 ── vmbr30 ┬─ cpanel3
 *                                    ├─ CPANEL
 *                                    ├─ caddy
 *                                    └─ gestion.adn-c.fr
 *
 * which is what the packets actually do. A bridge with no gateway hangs off the
 * host directly, as before.
 *
 * Pure and framework-free: no React, no d3, no DOM. Testable without a browser,
 * and the renderer could move to canvas without touching this file.
 */

import type { Bridge, Guest, HostNode, Snapshot } from '@proxmon/shared';

export type MapNodeKind = 'host' | 'bridge' | 'guest';

export interface MapNodeBase {
  id: string;
  kind: MapNodeKind;
  /** world coordinates of the glyph centre */
  x: number;
  y: number;
  /** hit-test radius (half-extent) of the glyph itself */
  r: number;
  label: string;
  /** name of the host tree this node belongs to */
  host: string;
  /** sits on, or is reached through, an internet-facing bridge */
  edge: boolean;
  /** how many hops from the host; drives column placement */
  depth: number;
  /** estimated width of the label drawn to the right, so bounds are honest */
  labelWidth: number;
}

export interface MapHostNode extends MapNodeBase {
  kind: 'host';
  data: HostNode;
}

export interface MapBridgeNode extends MapNodeBase {
  kind: 'bridge';
  data: Bridge;
  guestCount: number;
  /** synthesised branch for guests with no NIC at all */
  virtual: boolean;
  /** id of the guest this bridge sits behind, if any */
  gatewayId: string | null;
  /** name of that guest, for the inspector */
  gatewayName: string | null;
}

export interface MapGuestNode extends MapNodeBase {
  kind: 'guest';
  data: Guest;
  /** bridges this guest is the gateway for */
  gatewayFor: string[];
}

export type MapNode = MapHostNode | MapBridgeNode | MapGuestNode;

export interface MapLink {
  id: string;
  sourceId: string;
  targetId: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** 'primary' is the branch a node hangs from; 'secondary' is an extra NIC */
  kind: 'primary' | 'secondary';
  edge: boolean;
  /** bridge this link passes through, when bridge nodes are collapsed */
  viaBridge: string | null;
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface MapLayout {
  nodes: MapNode[];
  links: MapLink[];
  byId: Map<string, MapNode>;
  bounds: Bounds;
}

export interface LayoutOptions {
  /** hide stopped guests; changes the layout, not just the paint */
  hideStopped?: boolean;
  hideTemplates?: boolean;
  /** draw bridges as their own nodes; when false, links carry the bridge name */
  showBridges?: boolean;
}

// --- geometry -------------------------------------------------------------

const HOST_R = 30;
const BRIDGE_HALF_W = 8;
const BRIDGE_HALF_H = 14;
const GUEST_R = 10;

/** vertical slot per leaf; the single knob for how dense the map feels */
const LEAF_SPACING = 32;
/** gap between one host's tree and the next */
const TREE_GAP = 96;
/** gap between a node's label and the next column */
const COLUMN_GAP = 44;
const LABEL_GAP = 9;

const HOST_FONT = 13;
const BRIDGE_FONT = 11;
const GUEST_FONT = 11.5;
const ADDRESS_FONT = 9.5;

/** guests with no NIC still need somewhere to live */
const VIRTUAL_BRIDGE = '(no network)';

/**
 * Width of a string, without a DOM to measure in. Deliberately a slight
 * over-estimate: columns that are a few pixels too wide are invisible, columns
 * a few pixels too narrow collide.
 */
function textWidth(text: string, fontSize: number): number {
  return text.length * fontSize * 0.6;
}

// --- tree construction ----------------------------------------------------

interface TreeNode {
  id: string;
  kind: MapNodeKind;
  label: string;
  host: string;
  edge: boolean;
  depth: number;
  children: TreeNode[];
  /** bridge traversed on the link from the parent, when bridges are collapsed */
  viaBridge: string | null;

  hostData?: HostNode;
  bridgeData?: Bridge;
  guestData?: Guest;
  guestCount?: number;
  virtual?: boolean;
  gatewayId?: string | null;
  gatewayName?: string | null;
  gatewayFor?: string[];

  // filled in by the layout passes
  x: number;
  y: number;
  halfW: number;
  halfH: number;
  labelWidth: number;
}

interface HostPlan {
  root: TreeNode;
  /** guest id -> extra bridges it touches that are not its tree parent */
  extraAttachments: Map<string, string[]>;
  /** bridge name -> node id, for resolving secondary links */
  bridgeNodeIds: Map<string, string>;
}

export function computeLayout(snapshot: Snapshot, options: LayoutOptions = {}): MapLayout {
  const showBridges = options.showBridges !== false;

  const guestsByHost = new Map<string, Guest[]>();
  for (const guest of snapshot.guests) {
    if (options.hideTemplates && guest.template) continue;
    if (options.hideStopped && guest.status !== 'running') continue;
    const list = guestsByHost.get(guest.node) ?? [];
    list.push(guest);
    guestsByHost.set(guest.node, list);
  }

  const plans = snapshot.hosts.map((host) =>
    buildHostTree(host, guestsByHost.get(host.name) ?? [], showBridges),
  );

  const nodes: MapNode[] = [];
  const links: MapLink[] = [];
  const byId = new Map<string, MapNode>();

  // Lay each host tree out at the origin first, so we know how big it is
  // before deciding where to put it.
  const trees = plans.map((plan) => {
    measure(plan.root);
    const columns = columnPositions(plan.root);
    const height = position(plan.root, columns, 0);
    return { plan, width: treeWidth(plan.root), height };
  });

  for (const { plan, width, height } of packTrees(trees)) {
    void width;
    void height;
    flatten(plan.root, nodes, links, byId);
  }

  // Extra NICs: a guest touching a bridge that is not its tree parent gets a
  // dashed link. This is what makes a second firewall leg, or a VM straddling
  // two segments, visible without breaking the tree.
  for (const plan of plans) {
    for (const [guestId, bridges] of plan.extraAttachments) {
      const source = byId.get(guestId);
      if (!source) continue;
      for (const bridgeName of bridges) {
        const targetId = plan.bridgeNodeIds.get(bridgeName);
        const target = targetId ? byId.get(targetId) : undefined;
        if (!target || target.id === source.id) continue;
        links.push({
          id: `link2:${guestId}->${bridgeName}`,
          sourceId: source.id,
          targetId: target.id,
          x1: source.x,
          y1: source.y,
          x2: target.x,
          y2: target.y,
          kind: 'secondary',
          edge: target.kind === 'bridge' ? target.data.edge : false,
          viaBridge: bridgeName,
        });
      }
    }
  }

  return { nodes, links, byId, bounds: boundsOf(nodes) };
}

/**
 * Work out, for one host, which bridge sits behind which guest.
 *
 * Breadth-first from the internet-facing bridges outwards: a bridge reached for
 * the first time through guest G is *behind* G. Bridges never reached this way
 * hang off the host directly.
 */
function buildHostTree(host: HostNode, guests: Guest[], showBridges: boolean): HostPlan {
  const bridgeByName = new Map<string, Bridge>();
  for (const bridge of host.bridges) bridgeByName.set(bridge.name, bridge);

  // Deterministic iteration order everywhere below, so the same cluster always
  // produces the same map and operators build spatial memory of it.
  const sortedGuests = [...guests].sort((a, b) => a.vmid - b.vmid || a.name.localeCompare(b.name));

  const bridgesOfGuest = new Map<string, string[]>();
  let needsVirtual = false;
  for (const guest of sortedGuests) {
    const names: string[] = [];
    for (const nic of guest.nics) {
      if (!names.includes(nic.bridge)) names.push(nic.bridge);
      if (!bridgeByName.has(nic.bridge)) {
        // A bridge the host never reported: a permissions gap, or a guest
        // configured against a segment that no longer exists. Inferring it
        // keeps the branch, and everything on it, on the map.
        bridgeByName.set(nic.bridge, syntheticBridge(host.name, nic.bridge, false));
      }
    }
    if (names.length === 0) needsVirtual = true;
    bridgesOfGuest.set(guest.id, names);
  }

  // Must exist before `allBridges` is frozen below, or guests with no network
  // interface have no branch to hang from and disappear from the map entirely.
  if (needsVirtual) bridgeByName.set(VIRTUAL_BRIDGE, syntheticBridge(host.name, VIRTUAL_BRIDGE, true));

  const allBridges = [...bridgeByName.values()].sort((a, b) => {
    if (a.edge !== b.edge) return a.edge ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { numeric: true });
  });

  // --- BFS: depth and gateway per bridge ---------------------------------
  const depth = new Map<string, number>();
  const gateway = new Map<string, Guest>();

  const roots = allBridges.filter((b) => b.edge);
  const queue: string[] = [];
  for (const bridge of (roots.length ? roots : allBridges)) {
    depth.set(bridge.name, 0);
    queue.push(bridge.name);
  }

  while (queue.length > 0) {
    const current = queue.shift() as string;
    const currentDepth = depth.get(current) ?? 0;
    for (const guest of sortedGuests) {
      const touched = bridgesOfGuest.get(guest.id) ?? [];
      if (!touched.includes(current)) continue;
      for (const next of touched) {
        if (depth.has(next)) continue;
        depth.set(next, currentDepth + 1);
        gateway.set(next, guest);
        queue.push(next);
      }
    }
  }

  // Anything unreachable (an isolated segment with no router) hangs off the host.
  for (const bridge of allBridges) if (!depth.has(bridge.name)) depth.set(bridge.name, 0);

  // --- placement: each guest hangs off its most upstream bridge ------------
  const homeBridge = new Map<string, string>();
  const extraAttachments = new Map<string, string[]>();

  for (const guest of sortedGuests) {
    const touched = bridgesOfGuest.get(guest.id) ?? [];
    if (touched.length === 0) {
      homeBridge.set(guest.id, VIRTUAL_BRIDGE);
      continue;
    }
    const home = [...touched].sort((a, b) => {
      const da = depth.get(a) ?? 0;
      const db = depth.get(b) ?? 0;
      if (da !== db) return da - db;
      const ea = bridgeByName.get(a)?.edge ? 0 : 1;
      const eb = bridgeByName.get(b)?.edge ? 0 : 1;
      if (ea !== eb) return ea - eb;
      return a.localeCompare(b, undefined, { numeric: true });
    })[0] as string;
    homeBridge.set(guest.id, home);
    const extras = touched.filter((b) => b !== home && gateway.get(b)?.id !== guest.id);
    if (extras.length) extraAttachments.set(`guest:${guest.id}`, extras);
  }

  // --- assemble ------------------------------------------------------------
  const bridgeNodeIds = new Map<string, string>();

  const guestsOnBridge = (name: string): Guest[] =>
    sortedGuests.filter((g) => homeBridge.get(g.id) === name);

  const bridgesBehind = (guest: Guest): Bridge[] =>
    allBridges
      .filter((b) => gateway.get(b.name)?.id === guest.id)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  /**
   * @param expandBridges when false, bridges behind this guest are collapsed
   *   away and their guests become direct children, with the bridge name
   *   riding on the link instead.
   */
  const guestNode = (guest: Guest, nodeDepth: number, via: string | null, expandBridges: boolean): TreeNode => {
    const behind = bridgesBehind(guest);
    const node: TreeNode = {
      id: `guest:${guest.id}`,
      kind: 'guest',
      label: guest.name,
      host: host.name,
      edge: guest.edge,
      depth: nodeDepth,
      children: [],
      viaBridge: via,
      guestData: guest,
      gatewayFor: behind.map((b) => b.name),
      x: 0,
      y: 0,
      halfW: GUEST_R,
      halfH: GUEST_R,
      labelWidth: 0,
    };

    for (const bridge of behind) {
      if (expandBridges) {
        node.children.push(bridgeNode(bridge, nodeDepth + 1));
      } else {
        bridgeNodeIds.set(bridge.name, node.id);
        for (const child of guestsOnBridge(bridge.name)) {
          node.children.push(guestNode(child, nodeDepth + 1, bridge.name, false));
        }
      }
    }
    return node;
  };

  const bridgeNode = (bridge: Bridge, nodeDepth: number): TreeNode => {
    const children = guestsOnBridge(bridge.name);
    const gatewayGuest = gateway.get(bridge.name);
    const node: TreeNode = {
      id: `bridge:${bridge.id}`,
      kind: 'bridge',
      label: bridge.name,
      host: host.name,
      edge: bridge.edge,
      depth: nodeDepth,
      children: [],
      viaBridge: null,
      bridgeData: bridge,
      guestCount: children.length,
      virtual: bridge.name === VIRTUAL_BRIDGE,
      gatewayId: gatewayGuest ? `guest:${gatewayGuest.id}` : null,
      gatewayName: gatewayGuest?.name ?? null,
      x: 0,
      y: 0,
      halfW: BRIDGE_HALF_W,
      halfH: BRIDGE_HALF_H,
      labelWidth: 0,
    };
    bridgeNodeIds.set(bridge.name, node.id);
    for (const guest of children) node.children.push(guestNode(guest, nodeDepth + 1, null, true));
    return node;
  };

  const root: TreeNode = {
    id: `host:${host.id}`,
    kind: 'host',
    label: host.name,
    host: host.name,
    edge: host.bridges.some((b) => b.edge),
    depth: 0,
    children: [],
    viaBridge: null,
    hostData: host,
    x: 0,
    y: 0,
    halfW: HOST_R,
    halfH: HOST_R,
    labelWidth: 0,
  };

  // Bridges nobody routes to are the host's own segments; everything else is
  // reached through the guest that gateways it.
  const topBridges = allBridges.filter((b) => !gateway.has(b.name));

  if (showBridges) {
    for (const bridge of topBridges) root.children.push(bridgeNode(bridge, 1));
  } else {
    for (const bridge of topBridges) {
      bridgeNodeIds.set(bridge.name, root.id);
      for (const guest of guestsOnBridge(bridge.name)) {
        root.children.push(guestNode(guest, 1, bridge.name, false));
      }
    }
  }

  return { root, extraAttachments, bridgeNodeIds };
}

function syntheticBridge(node: string, name: string, virtual: boolean): Bridge {
  return {
    id: `${node}/${name}`,
    name,
    node,
    active: !virtual,
    autostart: false,
    cidr: null,
    gateway: null,
    ports: [],
    vlanAware: false,
    comment: virtual ? 'guests with no network interface' : 'not reported by this host',
    edge: false,
    // A bridge the host never reported has no counters to attribute to it.
    traffic: null,
    capacity: null,
    saturation: null,
  };
}

// --- layout passes --------------------------------------------------------

/** Estimate each node's label extent, so columns and bounds account for it. */
function measure(node: TreeNode): void {
  const font = node.kind === 'host' ? HOST_FONT : node.kind === 'bridge' ? BRIDGE_FONT : GUEST_FONT;
  let width = textWidth(node.label, font);

  if (node.kind === 'guest' && node.guestData?.edge) {
    width = Math.max(width, textWidth(edgeAddressText(node.guestData), ADDRESS_FONT));
  }
  if (node.kind === 'bridge' && node.guestCount) {
    width = Math.max(width, textWidth(`${node.label}  ${node.guestCount}`, BRIDGE_FONT));
  }

  node.labelWidth = width;
  for (const child of node.children) measure(child);
}

/** x for each depth: the previous column plus its widest label. */
function columnPositions(root: TreeNode): number[] {
  const widest: number[] = [];
  const walk = (node: TreeNode): void => {
    const extent = node.halfW + LABEL_GAP + node.labelWidth;
    widest[node.depth] = Math.max(widest[node.depth] ?? 0, extent);
    for (const child of node.children) walk(child);
  };
  walk(root);

  const columns: number[] = [];
  let x = 0;
  for (let d = 0; d < widest.length; d += 1) {
    columns[d] = x;
    x += (widest[d] ?? 0) + COLUMN_GAP;
  }
  return columns;
}

/**
 * Classic tidy tree: leaves take the next vertical slot, parents centre on
 * their children. Returns the total height consumed.
 */
function position(root: TreeNode, columns: number[], offsetY: number): number {
  let cursor = offsetY;

  const walk = (node: TreeNode): void => {
    node.x = columns[node.depth] ?? node.depth * 180;
    if (node.children.length === 0) {
      node.y = cursor;
      cursor += LEAF_SPACING;
      return;
    }
    for (const child of node.children) walk(child);
    const first = node.children[0] as TreeNode;
    const last = node.children[node.children.length - 1] as TreeNode;
    node.y = (first.y + last.y) / 2;
  };

  walk(root);
  return Math.max(cursor - offsetY, HOST_R * 2);
}

function flatten(node: TreeNode, nodes: MapNode[], links: MapLink[], byId: Map<string, MapNode>): void {
  const base = {
    id: node.id,
    x: node.x,
    y: node.y,
    label: node.label,
    host: node.host,
    edge: node.edge,
    depth: node.depth,
    labelWidth: node.labelWidth,
  };

  let mapNode: MapNode;
  if (node.kind === 'host' && node.hostData) {
    mapNode = { ...base, kind: 'host', r: HOST_R, data: node.hostData };
  } else if (node.kind === 'bridge' && node.bridgeData) {
    mapNode = {
      ...base,
      kind: 'bridge',
      r: BRIDGE_HALF_H,
      data: node.bridgeData,
      guestCount: node.guestCount ?? 0,
      virtual: node.virtual ?? false,
      gatewayId: node.gatewayId ?? null,
      gatewayName: node.gatewayName ?? null,
    };
  } else if (node.guestData) {
    mapNode = { ...base, kind: 'guest', r: GUEST_R, data: node.guestData, gatewayFor: node.gatewayFor ?? [] };
  } else {
    return;
  }

  nodes.push(mapNode);
  byId.set(mapNode.id, mapNode);

  for (const child of node.children) {
    links.push({
      id: `link:${node.id}->${child.id}`,
      sourceId: node.id,
      targetId: child.id,
      x1: node.x,
      y1: node.y,
      x2: child.x,
      y2: child.y,
      kind: 'primary',
      edge: child.edge && node.edge,
      viaBridge: child.viaBridge,
    });
    flatten(child, nodes, links, byId);
  }
}

interface PlacedTree {
  plan: HostPlan;
  width: number;
  height: number;
}

/** Rightmost extent of a positioned tree, labels included. */
function treeWidth(root: TreeNode): number {
  let max = 0;
  const walk = (node: TreeNode): void => {
    max = Math.max(max, node.x + node.halfW + LABEL_GAP + node.labelWidth);
    for (const child of node.children) walk(child);
  };
  walk(root);
  return max;
}

function translate(node: TreeNode, dx: number, dy: number): void {
  node.x += dx;
  node.y += dy;
  for (const child of node.children) translate(child, dx, dy);
}

/**
 * Pack the host trees into columns.
 *
 * Stacking every host in a single column is fine for three hosts and useless
 * for eleven: the map ends up a mile tall and a few hundred pixels wide, so
 * fit-to-view shrinks it to nothing. Choosing a column count that targets a
 * screen-ish aspect ratio keeps the whole estate legible at one zoom level.
 *
 * Trees keep their reading order as much as balancing allows, and the packing
 * is deterministic so the map does not reshuffle between polls.
 */
function packTrees(trees: PlacedTree[]): PlacedTree[] {
  if (trees.length === 0) return trees;

  const totalHeight = trees.reduce((sum, t) => sum + t.height + TREE_GAP, 0);
  const widest = Math.max(...trees.map((t) => t.width)) + TREE_GAP;

  // aspect = (k * widest) / (totalHeight / k)  =>  k = sqrt(aspect * total / widest)
  const TARGET_ASPECT = 1.7;
  const columnCount = Math.max(
    1,
    Math.min(trees.length, Math.round(Math.sqrt((TARGET_ASPECT * totalHeight) / widest))),
  );

  // Assign largest-first (classic LPT scheduling): assigning in host order
  // lets one big tree dominate a column and undoes the balancing.
  const byDescendingHeight = [...trees].sort(
    (a, b) => b.height - a.height || a.plan.root.label.localeCompare(b.plan.root.label),
  );

  const columns: PlacedTree[][] = Array.from({ length: columnCount }, () => []);
  const columnHeights = new Array<number>(columnCount).fill(0);

  for (const tree of byDescendingHeight) {
    let shortest = 0;
    for (let i = 1; i < columnCount; i += 1) {
      if ((columnHeights[i] ?? 0) < (columnHeights[shortest] ?? 0)) shortest = i;
    }
    columns[shortest]?.push(tree);
    columnHeights[shortest] = (columnHeights[shortest] ?? 0) + tree.height + TREE_GAP;
  }

  // Within a column, stack by host name so the map reads predictably even
  // though the column assignment was driven by size.
  columns.forEach((column, index) => {
    column.sort((a, b) => a.plan.root.label.localeCompare(b.plan.root.label, undefined, { numeric: true }));
    let y = 0;
    for (const tree of column) {
      translate(tree.plan.root, index * widest, y);
      y += tree.height + TREE_GAP;
    }
  });

  return trees;
}

/** The address shown under an exposed guest; mirrors the renderer. */
export function edgeAddressText(guest: Guest): string {
  const nic = guest.nics.find((n) => n.edge) ?? guest.nics[0];
  if (!nic) return '';
  if (nic.ip) return nic.ip.address;
  return nic.mac ? nic.mac.toUpperCase() : 'no address';
}

function boundsOf(nodes: MapNode[]): Bounds {
  if (nodes.length === 0) return { minX: -400, minY: -300, maxX: 400, maxY: 300 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const node of nodes) {
    minX = Math.min(minX, node.x - node.r);
    minY = Math.min(minY, node.y - node.r);
    // Labels extend to the right, and the address adds a second line below.
    maxX = Math.max(maxX, node.x + node.r + LABEL_GAP + node.labelWidth);
    maxY = Math.max(maxY, node.y + node.r + 12);
  }
  const pad = 60;
  return { minX: minX - pad, minY: minY - pad, maxX: maxX + pad, maxY: maxY + pad };
}
